/**
 * RLQ — LobbyQueueCard
 *
 * Modern card surface for the Lobby Queue list. Card anatomy:
 *
 *   ┌──────────────────────────────────────────┐
 *   │  ◯ Initials   Display name        ◷ wait │  ← identity strip
 *   │              Subtitle / hint              │
 *   ├──────────────────────────────────────────┤
 *   │  Reason chip  ·  permission gate chip     │  ← intent
 *   ├──────────────────────────────────────────┤
 *   │  ◉ Officer assigned / "Up next"           │  ← assignment
 *   │  Branch · Channel badge                    │  ← footer
 *   └──────────────────────────────────────────┘
 *
 * Per Bill 2026-04-23: NEVER surface sessionId / cartId / cifId / officer
 * code; only humanized values.
 */

import { Clock, MapPin, MonitorSmartphone, PhoneCall, Smartphone, UserCheck, Wifi } from '@retail-ops/panel-shell/fi';

import { Pill } from '@/components/primitives';
import type { LobbyChannel, LobbyVisitor, LobbyWaitTier } from '../shared/lobbyData';

export interface LobbyQueueCardProps {
  visitor: LobbyVisitor;
  /** Whether the active user has permission to claim this visitor. */
  canClaim: boolean;
  /** Tooltip / aria-label when claim is disabled. */
  claimDisabledReason?: string;
  /** Click → navigate into the target module. */
  onClaim: (v: LobbyVisitor) => void;
  /**
   * If provided, the card is selectable (Management view). Click the avatar
   * to toggle selection.
   */
  selected?: boolean;
  onToggleSelect?: (v: LobbyVisitor) => void;
}

const TIER_LABEL: Record<LobbyWaitTier, string> = {
  fresh: 'Just arrived',
  normal: 'Waiting',
  warning: 'Waiting longer than usual',
  breached: 'SLA breach — escalate',
};

const CHANNEL_META: Record<LobbyChannel, { label: string; icon: JSX.Element }> = {
  'walk-in': { label: 'Walk-in', icon: <UserCheck size={11} /> },
  online: { label: 'Online', icon: <Wifi size={11} /> },
  kiosk: { label: 'Kiosk', icon: <MonitorSmartphone size={11} /> },
  phone: { label: 'Phone', icon: <PhoneCall size={11} /> },
  appt: { label: 'Appointment', icon: <Smartphone size={11} /> },
};

export function LobbyQueueCard({
  visitor,
  canClaim,
  claimDisabledReason,
  onClaim,
  selected,
  onToggleSelect,
}: LobbyQueueCardProps): JSX.Element {
  const tierLabel = TIER_LABEL[visitor.waitTier];
  const channel = CHANNEL_META[visitor.channel];
  const claimable = canClaim && !visitor.assignedOfficerName;

  return (
    <article
      className={[
        'lobby-card',
        `lobby-card--${visitor.waitTier}`,
        visitor.assignedOfficerName ? 'lobby-card--claimed' : 'lobby-card--unclaimed',
        selected ? 'is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={(ev) => {
        if (!claimable) return;
        // Avoid hijacking clicks that originated on the avatar (selection toggle)
        // or any explicit button (claim CTA already handles itself).
        const target = ev.target as HTMLElement;
        if (target.closest('button') || target.closest('[data-stop-card-click]')) return;
        onClaim(visitor);
      }}
      role={claimable ? 'button' : undefined}
      tabIndex={claimable ? 0 : -1}
      onKeyDown={(ev) => {
        if (!claimable) return;
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          onClaim(visitor);
        }
      }}
      aria-label={`${visitor.displayName} — ${visitor.reasonLabel} — ${visitor.waitLabel}`}
    >
      {/* Identity strip ---------------------------------------------------- */}
      <header className="lobby-card__identity">
        {onToggleSelect ? (
          <button
            type="button"
            data-stop-card-click
            className={`lobby-card__avatar ${selected ? 'is-selected' : ''}`}
            onClick={() => onToggleSelect(visitor)}
            aria-label={selected ? 'Deselect visitor' : 'Select visitor'}
            aria-pressed={!!selected}
          >
            {selected ? '✓' : visitor.initials}
          </button>
        ) : (
          <div className="lobby-card__avatar" aria-hidden>
            {visitor.initials}
          </div>
        )}
        <div className="lobby-card__identity-text">
          <h3 className="lobby-card__name" title={visitor.displayName}>
            {visitor.displayName}
          </h3>
          {visitor.subtitle && (
            <span className="lobby-card__subtitle">{visitor.subtitle}</span>
          )}
        </div>
        <div
          className={`lobby-card__wait lobby-card__wait--${visitor.waitTier}`}
          title={tierLabel}
          aria-label={`${visitor.waitLabel} ${tierLabel}`}
        >
          <Clock size={12} aria-hidden />
          <span>{visitor.waitLabel}</span>
        </div>
      </header>

      {/* Intent line ------------------------------------------------------- */}
      <div className="lobby-card__intent">
        <span className="lobby-card__reason">{visitor.reasonLabel}</span>
        {visitor.requiredPermission !== 'Any' && (
          <Pill variant={canClaim ? 'info' : 'muted'}>
            {visitor.requiredPermission}
          </Pill>
        )}
      </div>

      {/* Assignment line --------------------------------------------------- */}
      <div className="lobby-card__assignment">
        {visitor.assignedOfficerName ? (
          <>
            <span className="lobby-card__assignment-icon" aria-hidden>
              <UserCheck size={12} />
            </span>
            <span className="lobby-card__assignment-label">
              With <strong>{visitor.assignedOfficerName}</strong>
            </span>
          </>
        ) : (
          <button
            type="button"
            className="lobby-card__claim"
            data-stop-card-click
            disabled={!canClaim}
            title={canClaim ? 'Claim this visitor' : claimDisabledReason}
            onClick={() => onClaim(visitor)}
          >
            <UserCheck size={12} aria-hidden />
            Claim
          </button>
        )}
      </div>

      {/* Footer ------------------------------------------------------------ */}
      <footer className="lobby-card__footer">
        <span className="lobby-card__footer-item" title={visitor.branchName}>
          <MapPin size={11} aria-hidden /> {visitor.branchName}
        </span>
        <span className="lobby-card__channel">
          {channel.icon}
          <span>{channel.label}</span>
        </span>
      </footer>
    </article>
  );
}
